import isSvg from "is-svg";
import React, {FC, Fragment, SyntheticEvent, useEffect, useState} from "react";
import {ImagePlaceholder} from "../ImagePlaceholder";
import {useJCommonContext} from "../JCommonProvider";
import {breakpointsMin} from "../breakpoints";
import {Breakpoint, CMSImage} from "../models";
import './JCMSImage.scss';

export function cmsImageIsObject(image: any): image is CMSImage {
    return image?.hasOwnProperty('src')
}

export function cmsImageIsArray(image: any): image is CMSImage[] {
    return Array.isArray(image) && !!image.length
}

export interface JCMSImageProps {
    /**
     * List of cms image
     * @desciption you can pass an image cms object temporarily, in the future it will only accept an array
     * @type {(CMSImage[] | CMSImage)}
     * @memberof JCMSImageProps
     */
    image?: CMSImage[] | CMSImage

    onError?: (event: SyntheticEvent<HTMLImageElement>) => void

    onLoad?: (event: SyntheticEvent<HTMLImageElement>) => void

    usePlaceholder?: boolean

    width?: number
    height?: number
    defaultImageWidth?: boolean
}

/**
 * Se ci sono più immagini con lo stesso breakpoint, assicurarci che vengono rimossi i duplicati.
 * Ordinare tutte le immagini in base al breakpoint, dal più grande al più piccolo.
 * Se ci sono più immagini con diversi breakpoint, l'immagine di default sarà quella con il breakpoint più piccolo.
 * Nel caso in cui non ci sia nessuna immagine con un breakpoint definito (ad esempio abbiamo soltanto una immagine), questa sarà quella di default.
 *
 * @param defaultImageSize
 * @param props
 * @constructor
 */
export const JCMSImage: FC<JCMSImageProps> = ({defaultImageWidth = true, ...props}) => {
    const { getImageURL } = useJCommonContext()
    const [error, setError] = useState<boolean>(false)

    const [images, setImages] = useState<CMSImage[] | undefined>(undefined);
    const [imagesWithBreakpoint, setImagesWithBreakpoint] = useState<CMSImage[] | undefined>([]);
    const [defaultImage, setDefaultImage] = useState<CMSImage | undefined>(undefined);

    useEffect(() => {
        if(!props?.image) return;

        const imagesUnique : CMSImage[]  =  [];
        const image = props.image
        if (cmsImageIsArray(image))
        {
            for (const imgArr of image) {
                const found =  imagesUnique.find(img => (img?.breakpoint === imgArr?.breakpoint) && ( (imgArr?.src?.length ?? 0) > 0) );
                if(!found && imgArr.src?.trim().length > 0){
                    imagesUnique.push(imgArr);
                }
            }
            setImages(imagesUnique.slice()
                .sort((a, b) => (breakpointsMin[b?.breakpoint?.toLowerCase() as Breakpoint]) > (breakpointsMin[a?.breakpoint?.toLowerCase() as Breakpoint]) ? 1 : -1)
                .map(img => (getImageObject(img))));
        }

        if (cmsImageIsObject(image))
        {
            console.warn(`CMS Image as CMSImage object is no longer supported: ${image.src}`);
            setImages((image?.src?.trim?.length ?? 0) > 0 ? [getImageObject(image)] : undefined)
        }


    }, [props?.image]);

    useEffect(()=>{
        if(!images || images.length === 0) return;

        let imagesWithBreakpoint =  images?.filter(i => i.breakpoint)

         let defaultImage = images?.find(i => !i.breakpoint)

        if(imagesWithBreakpoint.length > 0 ){

            let maxBreakPointImage: string | undefined = imagesWithBreakpoint[0]?.breakpoint?.toLowerCase(); //md
            let imageWithoutBreakPoint: CMSImage | undefined =   images?.find(i => !i.breakpoint)

            if(imagesWithBreakpoint.length === 1){
                defaultImage = imagesWithBreakpoint?.[imagesWithBreakpoint?.length -1];
                if(imageWithoutBreakPoint === undefined){
                    imagesWithBreakpoint.pop();
                }
            }

            if(!!imageWithoutBreakPoint){
                // Immagine con il breakpoint minore.
                defaultImage =  imagesWithBreakpoint?.[imagesWithBreakpoint?.length -1];

                let updateImage = true;

                switch (maxBreakPointImage) {
                    case 'xxs': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'xs' : [];
                        break;
                    case 'xs': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'sm' : [];
                        break;
                    case 'sm': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'md' : [];
                        break;
                    case 'md': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'lg' : [];
                        break;
                    case 'lg': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'xl' : [];
                        break;
                    case 'xl': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'xxl' : [];
                        break;
                    case 'xxl': imageWithoutBreakPoint ? imageWithoutBreakPoint.breakpoint = 'xxxl' : [];
                        break;
                    default:
                        updateImage = false;
                        break
                }

                if(updateImage){
                    imagesWithBreakpoint.unshift( imageWithoutBreakPoint as CMSImage);
                    imagesWithBreakpoint.pop();
                }

                if(!updateImage && imagesWithBreakpoint.length > 1
                    && defaultImage?.breakpoint === imagesWithBreakpoint[imagesWithBreakpoint.length -1].breakpoint ){
                    imagesWithBreakpoint.pop();
                }
            }
            else {
                defaultImage = (imagesWithBreakpoint?.length ?? 0 > 0) ? imagesWithBreakpoint?.[imagesWithBreakpoint?.length -1] : defaultImage;
                imagesWithBreakpoint.pop();
            }
        } else {
            defaultImage =  (imagesWithBreakpoint?.length ?? 0 > 0) ? imagesWithBreakpoint?.[imagesWithBreakpoint?.length -1] : defaultImage;
        }

        setImagesWithBreakpoint(imagesWithBreakpoint);
        setDefaultImage(defaultImage)

    },[images])


    function getImageObject(image: CMSImage) {
        return {
            ...image,
            breakpoint: image.breakpoint,
            src: isSvg(image.src) ? image.src : getImageURL(image.src)
        };
    }

    const onLoadImage = (event: SyntheticEvent<HTMLImageElement>) => {
        setError(false)
        props.onLoad && props.onLoad(event)
    }

    const onErrorImage = (event: SyntheticEvent<HTMLImageElement>) => {
        setError(true)
        props.onError && props.onError(event)
    }

    const imageIsSVG = defaultImage && isSvg(defaultImage.src);

    return (
        <Fragment>
            {
                (!!imagesWithBreakpoint?.length || defaultImage) && (
                    <picture className={`jcms-image ${ (defaultImageWidth) ? 'jcms-image--default-width' : 'jcms-image--max-width'}`}>
                        {
                            imagesWithBreakpoint
                                ?.map(({ src, breakpoint }, i) => {
                                    return (
                                        <source
                                            key={i}
                                            media={`(min-width: ${breakpointsMin[breakpoint as Breakpoint]}px)`}
                                            srcSet={getImageURL(src)}
                                        />
                                    )
                                })
                        }
                        {
                            props.usePlaceholder && (error || !defaultImage?.src) && (
                                <ImagePlaceholder className="jcms-image-placeholder" />
                            )
                        }
                        {
                            defaultImage && !error && (
                                imageIsSVG ? (
                                    <span aria-hidden={true} dangerouslySetInnerHTML={{ __html: defaultImage.src }}></span>
                                ) : (

                                    <img
                                        aria-hidden={true}
                                        style={{ visibility: error ? 'hidden' : 'visible' }}
                                        onLoad={onLoadImage}
                                        onError={onErrorImage}
                                        width={props?.width ?? '100%'}
                                        height={props?.height ?? '100%'}
                                        src={getImageURL(defaultImage.src)}
                                        alt={defaultImage?.breakpoint ?? 'image'}
                                    />
                                )
                            )
                        }
                    </picture>
                )
            }
        </Fragment>
    )
}

